/** @file The selection without the ids that left the document (bones, slots, meshes, keys). */
import type { Doc } from '@pose2d/format/types';
import type { SelectionSlice } from './types';

const DRAW_ORDER_TARGET = '#drawOrder';

/** True when the target of a key still exists (a bone, slot, IK constraint, attachment or event; the draw order has none). */
function targetAlive(doc: Doc, target: string): boolean {
  return target === DRAW_ORDER_TARGET || [doc.bones, doc.slots, doc.constraints, doc.attachments, doc.events].some((table) => Object.hasOwn(table, target));
}

/** The pruned selection, or null when every id in `selection` is still alive (so the caller writes nothing). */
export function pruneSelection(selection: SelectionSlice, doc: Doc): SelectionSlice | null {
  const bones = selection.bones.filter((id) => Object.hasOwn(doc.bones, id));
  const slots = selection.slots.filter((id) => Object.hasOwn(doc.slots, id));
  const vertices = selection.vertices.filter(([mesh]) => Object.hasOwn(doc.attachments, mesh));
  const keys = selection.keys.filter((k) => Object.hasOwn(doc.animations, k.animation) && targetAlive(doc, k.target));
  const same = bones.length === selection.bones.length && slots.length === selection.slots.length
    && vertices.length === selection.vertices.length && keys.length === selection.keys.length;
  return same ? null : { bones, slots, vertices, keys };
}
